/**
 * MFA Method List
 *
 * Card-style method selector for multi-method MFA. Shows available
 * methods (TOTP, SMS, Email) with labels and masked contact hints.
 *
 * @module @puffin-core/mfa-ui/internal/MfaMethodList
 */

import PropTypes from 'prop-types';

/**
 * Method display configuration.
 * @param {string} method - Method key
 * @param {Function} t - Translation function
 * @param {Object} params - { maskedPhone, maskedEmail }
 * @returns {{ label: string, hint: string }}
 */
function getMethodDisplay(method, t, params) {
  switch (method) {
    case 'totp':
      return { label: t('method_totp'), hint: t('method_totp_hint') };
    case 'sms':
      return { label: t('method_sms'), hint: params.maskedPhone ? `***${params.maskedPhone}` : '' };
    case 'email':
      return { label: t('method_email'), hint: params.maskedEmail || '' };
    // ECOSYSTEM-EMAIL-001 Q-EE-34 P5: offered only where the host can run the ceremony (MfaVerification).
    case 'passkey':
      return { label: t('method_passkey'), hint: t('method_passkey_hint') };
    default:
      return { label: method, hint: '' };
  }
}

/**
 * @param {Object} props
 * @param {string[]} props.methods - Available MFA methods
 * @param {string|null} props.selectedMethod - Currently selected method
 * @param {(method: string) => void} props.onSelectMethod - Selection handler
 * @param {Function} props.t - Translation function
 * @param {string} [props.maskedPhone] - Masked phone for SMS hint
 * @param {string} [props.maskedEmail] - Masked email for Email hint
 * @returns {React.JSX.Element}
 */
export function MfaMethodList({
  methods,
  selectedMethod,
  onSelectMethod,
  t,
  maskedPhone,
  maskedEmail,
}) {
  return (
    <div className="mfa-method-selector" role="group" aria-label={t('title_methods')}>
      {methods.map((method) => {
        const { label, hint } = getMethodDisplay(method, t, { maskedPhone, maskedEmail });
        const isSelected = method === selectedMethod;

        return (
          <button
            key={method}
            type="button"
            className={`mfa-method-btn${isSelected ? ' mfa-method-btn-active' : ''}`}
            onClick={() => onSelectMethod(method)}
            aria-pressed={isSelected}
            data-testid={`mfa-method-btn-${method}`}
          >
            <span className="mfa-method-btn-label">{label}</span>
            {hint && <span className="mfa-method-btn-hint">{hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

MfaMethodList.propTypes = {
  methods: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedMethod: PropTypes.string,
  onSelectMethod: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  maskedPhone: PropTypes.string,
  maskedEmail: PropTypes.string,
};
